import { Box } from "@mui/material";
import React from "react";
import { HeaderText } from "../../shared/text/HeaderText.styled";
import FullScreenDrawerLayout from "../../shared/layout/FullScreenDrawerLayout";
import TextIconButton from "../../shared/buttons/TextIconButton";
import { QTO_SOURCES, QTO_SOURCE_ORDER } from "../../../integrations/qto/registry";
import { useDatasources } from "../../context/DatasourceContext";
import { useLoading } from "../../context/LoadingContext";
import { useDrawer } from "../../context/DrawerContext";
import { useQtoSourcesEnabled } from "../../hooks/useQtoSources";
import { useOst } from "./ost/OstContext";
import { useAto } from "./ato/AtoContext";
import { useTogal } from "./togal/TogalContext";
import TogalList from "./togal/TogalList";
import ATOList from "./ato/ATOList";
import OSTUpload from "./ost/OSTUpload";
import { TAKE_OFF_SOURCE_TYPE_LABEL } from "../../constants";
import { ediphiPalette } from "../../styles/colors";

const TakeOffDatasourceDrawer = () => {
  const { hasDatasource } = useDatasources();
  const { globalLoading } = useLoading();
  const enabledById = useQtoSourcesEnabled();
  const { ostState } = useOst();
  const { atoState } = useAto();
  const { togalState } = useTogal();
  // Each source runs its submit through its own context state rather than withLoading, so
  // globalLoading alone leaves the tiles live through an upload or a set/package pull.
  const sourceBusy = ostState === "loading" || atoState === "loading" || togalState === "loading";
  const { isOpen, mode, selection: selectedSourceType, select, close } = useDrawer("takeOff");

  const renderSourceContent = () => {
    switch (selectedSourceType) {
      case "togal":
        return <TogalList />;
      case "ato":
        return <ATOList />;
      case "ost":
        return <OSTUpload />;
      default:
        return null;
    }
  };

  const generateDrawerTitle = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <HeaderText>
        {mode === "edit" && selectedSourceType
          ? `Update ${QTO_SOURCES[selectedSourceType].label}`
          : "Add External Takeoff Source"}
      </HeaderText>
    </Box>
  );

  return (
    <FullScreenDrawerLayout isDrawerOpen={isOpen} onDrawerClose={close} drawerTitle={generateDrawerTitle()}>
      <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
        {mode === "add" && (
          <Box
            role="group"
            aria-label={TAKE_OFF_SOURCE_TYPE_LABEL}
            sx={{
              p: 2,
              flexShrink: 0,
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 0.5,
              borderBottom: `1px solid ${ediphiPalette.borderDivider}`,
            }}
          >
            {QTO_SOURCE_ORDER.map((type) => (
              <TextIconButton
                key={type}
                isSelected={selectedSourceType === type}
                text={QTO_SOURCES[type].label}
                onClick={() => select(type)}
                // The flag gates presence, which a source that is merely unconnected also fails, so it
                // has to be checked separately or a flag-off source looks available.
                disabled={hasDatasource("takeOff", type) || !enabledById[type] || globalLoading || sourceBusy}
              />
            ))}
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {selectedSourceType ? renderSourceContent() : null}
        </Box>
      </Box>
    </FullScreenDrawerLayout>
  );
};

export default TakeOffDatasourceDrawer;
